/* Note: Images are stored in the public folder - this allows us to use a link
   with the href pointing at the image's public folder URL.  If, in the future,
   images are rendered instead of linked to, the images should be moved out of
   the public folder. */
enum GAImageFileName {
  SEARCH_GALLERY = "search-gallery.png",
  CLICK_NEW = "click-new.png",
  TYPE_TRIM_QUERY = "type-trim-query.png",
  SELECT_TRIM_QUERY = "select-trim-query.png",
  ADD_TO_WORKSPACE = "add-to-workspace.png",
  SELECT_PAGE_URL = "select-page-url.png",
  PASTE_NORTHBEAM_VARIABLES = "paste-northbeam-variables.png",
  SELECT_UTM_EXCLUSION = "select-utm-exclusion.png",
}

const GOOGLE_DOCS_URL =
  "https://docs.google.com/document/d/15MPEfp1VRNSjn16SwdGGEcMQY6BQmMktMP61M6w1G0k/edit";

// Local/file scoped component - not meant for usage external to this file.
const GAInlineLink = ({
  filename,
  children,
  left = " ",
  right = " ",
}: {
  readonly filename: GAImageFileName | { href: string };
  readonly children: string;
  readonly left?: string | null | undefined;
  readonly right?: string | null | undefined;
}): JSX.Element => (
  <span>
    {left}
    <a
      href={
        typeof filename === "string"
          ? process.env.PUBLIC_URL + "/features/google-analytics/" + filename
          : filename.href
      }
      rel="noreferrer"
      target="_blank"
    >
      {children}
    </a>
    {right}
  </span>
);

type Note = { note: string };
type Instruction = JSX.Element | string | Note;

const instructionIsNote = (
  instruction: Instruction,
): instruction is { note: string } =>
  typeof instruction === "object" &&
  instruction !== null &&
  typeof (instruction as Note).note === "string";

interface GA4InstructionSetProps {
  readonly title: JSX.Element | string;
  readonly subTitle?: string;
  readonly instructions: Instruction[];
}

// Local/file scoped component - not meant for usage external to this file.
const GA4InstructionSet = ({
  title,
  subTitle,
  instructions,
}: GA4InstructionSetProps): JSX.Element => {
  return (
    <div className="flex flex-col gap-1">
      {typeof title === "string" ? (
        <p className="mb-0 font-semibold">{title}</p>
      ) : (
        title
      )}
      {subTitle && <p className="mb-0 italic">{subTitle}</p>}
      <ul className="list-disc pl-4 mb-0">
        {instructions.map((instruction, i) =>
          instructionIsNote(instruction) ? (
            <p className="mb-0 font-size-sm italic text-gray" key={i}>
              {instruction.note}
            </p>
          ) : (
            <li key={i}>{instruction}</li>
          ),
        )}
      </ul>
    </div>
  );
};

export const GA4ParameterExclusions = (): JSX.Element => (
  <div className="ga4-parameter-exclusions flex flex-col gap-4">
    <div className="flex flex-col">
      <h4 className="heading mx-0 mb-0 mt-0 font-weight-bold text-lg mb-2">
        For GA4
      </h4>
      <p className="text-md mb-1 underline">
        How to Remove Northbeam`s UTM Parameters from Your GA4
      </p>
      <p className="italic mb-0">
        {"If you currently use a GA dimension that pulls URL query parameters into " +
          /* eslint-disable-next-line quotes */
          'your reports (such as "query string" or "page ' +
          /* eslint-disable-next-line quotes */
          'location"), our additional parameters may cause interference. ' +
          "However, if your reports don`t contain a dimension that pulls query " +
          "strings, exclusions may not be necessary."}
      </p>
    </div>
    <div className="flex flex-col gap-3">
      <GA4InstructionSet
        title="Once you are in GTM go to Templates"
        instructions={[
          <span key="0">
            Click
            <GAInlineLink filename={GAImageFileName.SEARCH_GALLERY} right=".">
              Search Gallery
            </GAInlineLink>
          </span>,
          <span key="1">
            Search for
            <GAInlineLink filename={GAImageFileName.TYPE_TRIM_QUERY} right=".">
              Trim Query
            </GAInlineLink>
          </span>,
          {
            note:
              "It was created by Ayudante, a Tokyo-based consulting and " +
              "mobile software development company. Big thanks to them!",
          },
          <span key="4">
            Click on that and Choose
            <GAInlineLink filename={GAImageFileName.ADD_TO_WORKSPACE} right=".">
              Add to Workspace
            </GAInlineLink>
          </span>,
        ]}
      />
      <GA4InstructionSet
        title="Click on Variables"
        instructions={[
          <GAInlineLink
            key="0"
            filename={GAImageFileName.CLICK_NEW}
            left={null}
            right={null}
          >
            Click New
          </GAInlineLink>,
          <span key="1">
            Name the Variable: <code>UTM Exclusions</code>
          </span>,
          "Click Variable Configuration",
          <GAInlineLink
            key="3"
            filename={GAImageFileName.SELECT_TRIM_QUERY}
            left={null}
            right={null}
          >
            Select Trim Query
          </GAInlineLink>,
        ]}
      />
      <GA4InstructionSet
        title="In the Variable Configuration"
        instructions={[
          <span key="0">
            In the Target URL
            <GAInlineLink filename={GAImageFileName.SELECT_PAGE_URL} right=".">
              Select Page URL
            </GAInlineLink>
          </span>,
          {
            note:
              "Note: this variable does not affect the page URL your " +
              "visitors see in the browser address bar. It simply will " +
              "return a cleaner URL as a value.",
          },
          <span key="2">
            From here we will now
            <GAInlineLink filename={GAImageFileName.PASTE_NORTHBEAM_VARIABLES}>
              paste all of the Northbeam variables
            </GAInlineLink>
            individually from
            <GAInlineLink filename={{ href: GOOGLE_DOCS_URL }} right=". ">
              this list
            </GAInlineLink>
            Make sure not to copy the commas.
          </span>,
          {
            note:
              "We understand that this will be time-consuming, but will " +
              "ensure that you have the highest quality data reporting from " +
              "your site into GA4.",
          },
          "After you are done hit Save.",
        ]}
      />
      <GA4InstructionSet
        title={
          "Go back to Tags and select your GA4-Configuration Tag - the " +
          "one that added GA4 to your site."
        }
        subTitle={
          "If you are not currently running GA4 through Google Tag Manager " +
          "this process will not work."
        }
        instructions={[
          "In the Configuration",
          "Click the Fields to Set",
          <span key="2">
            Type in the following: <code>page_location</code>
          </span>,
          <span key="3">
            In the Value click the + button on the right and
            <GAInlineLink filename={GAImageFileName.SELECT_UTM_EXCLUSION}>
              select the UTM Exclusions variable
            </GAInlineLink>
            that you just created.
          </span>,
          <span key="4">
            Is GA4 installed on a single-page application? Then we would suggest
            setting the same <code>page_location</code> parameter in all GA4
            event tags as well.
          </span>,
        ]}
      />
      <p>
        At this point, you are done. From there you can check your GA4 the next
        day and choose that day`s data only and you should no longer see the
        Northbeam UTMs in your GA4 Data.
      </p>
    </div>
  </div>
);
